import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';


/**
 * Formatea un timestamp absoluto como tiempo relativo, traducible
 * ("hace 12 s", "hace 3 min", "hace 1 h"). Recibe `t` para i18n.
 */
function formatRelative(ts, now, t) {
  if (!ts) return '—';
  const diffMs = Math.max(0, now - ts);
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return t('hace {{n}} s', { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t('hace {{n}} min', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('hace {{n}} h', { n: h });
  const d = Math.floor(h / 24);
  return t('hace {{n}} d', { n: d });
}

/**
 * Header con selector de ventana, modo de auto-refresh, indicador de "ultima
 * actualizacion" en vivo y boton de refresh manual.
 *
 * Soporta dos modos de tiempo:
 *  - relativo (`window`): usado por defecto, se envia como ?window=15m, etc.
 *  - absoluto (`from`/`to`): toggle "Personalizado" que muestra dos
 *    datetime-local; al aplicar, envia ISO-8601 al backend e ignora `window`.
 */

const MetricsControls = ({
  window: currentWindow,
  from,
  to,
  refreshMs,
  refreshing,
  loading,
  lastUpdatedAt,
  timeDimension = 'finished_at',
  filterTenant = true,
  source = '',
  onChange,
  onRefresh,
}) => {
  const { t } = useTranslation();

  const WINDOW_PRESETS = [
    { id: '15m', label: t('15 min') },
    { id: '1h', label: t('1 hora') },
    { id: '6h', label: t('6 horas') },
    { id: '24h', label: t('24 horas') },
    { id: '7d', label: t('7 días') },
    { id: '30d', label: t('30 días') },
  ];

  const REFRESH_OPTIONS = [
    { id: 0, label: t('Manual') },
    { id: 10000, label: t('Cada 10s') },
    { id: 30000, label: t('Cada 30s') },
    { id: 60000, label: t('Cada 1 min') },
    { id: 300000, label: t('Cada 5 min') },
  ];

  // Buckets de origen que expone calidad-ia v2 (ver
  // delivery-quality/docs/calidad-ia-metrics-coordination.md). El valor
  // vacio en el dropdown significa "todos los origenes" (no se manda el
  // query param `?source=`).
  const SOURCE_OPTIONS = [
    { id: '', label: t('Todos los orígenes') },
    { id: 'live', label: t('Live (online)') },
    { id: 'batch', label: t('Batch (nightly)') },
    { id: 'backfill', label: t('Backfill') },
    { id: 'manual', label: t('Manual') },
    { id: 'reconciler', label: t('Reconciler (reintentos)') },
  ];

  const [now, setNow] = useState(() => Date.now());
  const [customMode, setCustomMode] = useState(Boolean(from && to));
  const [customFrom, setCustomFrom] = useState(
    from ? dayjs(from).format('YYYY-MM-DDTHH:mm') : dayjs().subtract(1, 'hour').format('YYYY-MM-DDTHH:mm'),
  );
  const [customTo, setCustomTo] = useState(
    to ? dayjs(to).format('YYYY-MM-DDTHH:mm') : dayjs().format('YYYY-MM-DDTHH:mm'),
  );

  // Tick por segundo solo para el "hace X s". Lightweight.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const relative = useMemo(() => formatRelative(lastUpdatedAt, now, t), [lastUpdatedAt, now, t]);

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const fromIso = dayjs(customFrom).toISOString();
    const toIso = dayjs(customTo).toISOString();
    onChange({ window: undefined, from: fromIso, to: toIso });
  };

  const selectWindow = (id) => {
    setCustomMode(false);
    onChange({ window: id, from: undefined, to: undefined });
  };

  const enterCustomMode = () => {
    setCustomMode(true);
  };

  const isLive = !!refreshMs && refreshMs > 0;

  return (
    <Card className="mb-3 ai-perf-controls">
      <CardBody>
        <Row className="g-3 align-items-end">
          {/* ── Ventana temporal ─────────────────────────────────────── */}
          <Col xs="12" lg="7">
            <Label className="form-label d-block">
              {t('Ventana temporal')}
              {!customMode && currentWindow && (
                <span className="text-muted ms-2" style={{ fontWeight: 400, fontSize: 12 }}>
                  ({currentWindow.endsWith('m') || currentWindow.endsWith('h')
                    ? t('último {{w}}', { w: currentWindow })
                    : t('últimos {{w}}', { w: currentWindow })})
                </span>
              )}
            </Label>
            <div className="d-flex flex-wrap gap-1">
              {WINDOW_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectWindow(p.id)}
                  className={
                    'btn btn-sm ai-perf-pill ' +
                    (!customMode && currentWindow === p.id
                      ? 'btn-primary'
                      : 'btn-outline-secondary')
                  }
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={enterCustomMode}
                className={
                  'btn btn-sm ai-perf-pill ' +
                  (customMode ? 'btn-primary' : 'btn-outline-secondary')
                }
              >
                <i className="fas fa-calendar-days me-1" />
                {t('Personalizado')}
              </button>
            </div>

            {customMode && (
              <Row className="g-2 mt-2 align-items-end">
                <Col xs="12" md="5">
                  <Label className="form-label" style={{ fontSize: 12 }}>
                    {t('Desde')}
                  </Label>
                  <Input
                    type="datetime-local"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </Col>
                <Col xs="12" md="5">
                  <Label className="form-label" style={{ fontSize: 12 }}>
                    {t('Hasta')}
                  </Label>
                  <Input
                    type="datetime-local"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </Col>
                <Col xs="12" md="2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    onClick={applyCustom}
                  >
                    {t('Aplicar')}
                  </button>
                </Col>
              </Row>
            )}
          </Col>

          {/* ── Auto-refresh + estado ─────────────────────────────────── */}
          <Col xs="12" lg="5">
            <Label className="form-label d-block">{t('Actualización automática')}</Label>
            <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
              <div className="d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{t('Eje temporal')}:</span>
                <Input
                  type="select"
                  bsSize="sm"
                  value={timeDimension}
                  onChange={(e) => onChange({ timeDimension: e.target.value })}
                  style={{ width: 'auto', fontSize: 12 }}
                  title={t('Sobre que campo se aplica la ventana al consultar calidad-ia. delivery_date alinea con la fecha real de entrega del paquete (comparable con Calidad de entregas).')}
                >
                  <option value="finished_at">finished_at</option>
                  <option value="created_at">created_at</option>
                  <option value="delivery_date">delivery_date</option>
                </Input>
              </div>
              <div className="d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{t('Origen')}:</span>
                <Input
                  type="select"
                  bsSize="sm"
                  value={source}
                  onChange={(e) => onChange({ source: e.target.value })}
                  style={{ width: 'auto', fontSize: 12 }}
                  title={t('Filtra por bucket de origen del job. Live = trafico real online; el resto son pipelines internos.')}
                >
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.id || 'all'} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              </div>
              <Label
                check
                style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 0 }}
              >
                <Input
                  type="checkbox"
                  checked={filterTenant}
                  onChange={(e) => onChange({ filterTenant: e.target.checked })}
                />{' '}
                {t('Solo mi empresa')}
              </Label>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Input
                type="select"
                value={refreshMs || 0}
                onChange={(e) => onChange({ refreshMs: Number(e.target.value) })}
                style={{ maxWidth: 160 }}
              >
                {REFRESH_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Input>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={onRefresh}
                disabled={loading || refreshing}
                title={t('Refrescar ahora')}
              >
                <i
                  className={
                    'fas fa-arrows-rotate ' + (refreshing ? 'ai-perf-spin' : '')
                  }
                />
                <span className="ms-1">{t('Refrescar')}</span>
              </button>

              <div className="ai-perf-status d-flex align-items-center gap-2 ms-auto">
                <span
                  className={
                    'ai-perf-dot ' +
                    (isLive
                      ? refreshing
                        ? 'ai-perf-dot--refreshing'
                        : 'ai-perf-dot--live'
                      : 'ai-perf-dot--idle')
                  }
                  title={isLive ? t('Auto-refresh activo') : t('Auto-refresh apagado')}
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  {refreshing ? t('Actualizando…') : `${t('Última act.')}: ${relative}`}
                </small>
              </div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default MetricsControls;
